import { ExternalLink } from 'lucide-react'
import { useState } from 'react'

import { PortalLink } from '../../models/insuranceApplication'
import { InsuranceIndication } from '../../models/insuranceIndication'
import { InsuranceQuote } from '../../models/insuranceQuote'
import { InsuranceIndicationStatus } from '../../models/insuranceTypes'
import { useInsuranceApplicationSidebarContext } from '../../routes/Private/InsuranceApplication'
import { ChatIndicationCard } from './ChatIndicationCard'
import { ChatIndicationDetails } from './ChatIndicationDetails'
import { IndicationDetailActions } from './IndicationDetailActions'

export { SIDEBAR_WIDTH } from './ChatIndicationDetails'

export interface ChatInsuranceListProps {
  indications: InsuranceIndication[]
  isStaff?: boolean
  isCaiAdmin?: boolean
  handleToggleSelect: (indication: InsuranceIndication) => void
  selected: InsuranceIndication[]
  showQuotes?: boolean
  onViewDetails?: (
    indicationOrQuote: InsuranceIndication | InsuranceQuote | null,
    indication: InsuranceIndication
  ) => void
  renderIndicationChildren?: (indication: InsuranceIndication) => React.ReactNode
  readonly?: boolean
  hideCheckbox?: boolean
  hideViewDetails?: boolean
  portalLinks?: PortalLink[]
  /** Needed for the card's Resubmit button. Falls back to the sidebar
   * context's applicationId where this list isn't rendered inside that
   * context's provider (e.g. the customer-facing Insurance page). */
  applicationId?: string
  /** Needed for the card's "Send to Corgi" action — omitted, it doesn't render. */
  orgId?: string
  /** Staff only: when the application last changed. Falls back to the sidebar context. */
  applicationEditedAt?: string | null
}

const TERMINAL_STATUSES: ReadonlySet<InsuranceIndicationStatus> = new Set([
  InsuranceIndicationStatus.DECLINED,
  InsuranceIndicationStatus.ERROR,
  InsuranceIndicationStatus.UNAVAILABLE,
  InsuranceIndicationStatus.CANCELED,
])

export function ChatInsuranceList({
  indications,
  isStaff,
  isCaiAdmin,
  handleToggleSelect,
  selected,
  showQuotes,
  onViewDetails,
  renderIndicationChildren,
  readonly,
  hideCheckbox: hideCheckboxProp,
  hideViewDetails,
  portalLinks,
  applicationId: applicationIdProp,
  orgId,
  applicationEditedAt: applicationEditedAtProp,
}: ChatInsuranceListProps) {
  const hideCheckbox = hideCheckboxProp ?? (showQuotes ? false : indications.length === 1)
  const {
    setSidebar,
    portalLinks: contextPortalLinks,
    applicationId: contextApplicationId,
    indications: liveIndications,
    applicationEditedAt: contextApplicationEditedAt,
  } = useInsuranceApplicationSidebarContext()

  // Use prop if provided, otherwise fall back to context
  const effectivePortalLinks = portalLinks ?? contextPortalLinks
  const applicationId = applicationIdProp ?? contextApplicationId
  const applicationEditedAt = applicationEditedAtProp ?? contextApplicationEditedAt

  // Chat-item indication payloads are serialized without request context, so
  // admin-only fields like submissionScreenshots are always empty there. Graft
  // them from the live application.indications (request-scoped, admin-gated).
  const liveById = new Map((liveIndications ?? []).map((i) => [i.id, i]))
  const enrichedIndications = indications.map((indication) => {
    const live = liveById.get(indication.id)
    if (!live) return indication
    return {
      ...indication,
      submissionScreenshots: live.submissionScreenshots?.length
        ? live.submissionScreenshots
        : indication.submissionScreenshots,
      additionalInfoRequested: live.additionalInfoRequested ?? indication.additionalInfoRequested,
      portalNoteUrl: live.portalNoteUrl ?? indication.portalNoteUrl,
      quoteDocuments: live.quoteDocuments?.length ? live.quoteDocuments : indication.quoteDocuments,
      checkoutEvidence: live.checkoutEvidence ?? indication.checkoutEvidence,
      declineReasonRaw: live.declineReasonRaw ?? indication.declineReasonRaw,
      declineDetail: live.declineDetail ?? indication.declineDetail,
      dateApplied: live.dateApplied || indication.dateApplied,
    }
  })
  const [currentIndication, setCurrentIndication] = useState<InsuranceIndication | InsuranceQuote | null>(
    null
  )

  function handleOpenIndicationDetails(
    indicationOrQuote: InsuranceIndication | InsuranceQuote | null,
    indication: InsuranceIndication
  ) {
    if (onViewDetails) {
      onViewDetails(indicationOrQuote, indication)
      return
    }
    setCurrentIndication(indicationOrQuote)
    setSidebar!(
      <ChatIndicationDetails
        isVisible={true}
        handleClose={handleCloseIndicationDetails}
        indication={indicationOrQuote}
        // The application view has no per-indication CTAs of its own, but the
        // links, screenshots and vendor id belong here just the same.
        actions={
          <IndicationDetailActions indication={indication} isStaff={isStaff} isCaiAdmin={isCaiAdmin} />
        }
      />
    )
  }

  function handleCloseIndicationDetails(indicationOrQuote: InsuranceIndication | InsuranceQuote | null) {
    setSidebar!(
      <ChatIndicationDetails
        isVisible={false}
        handleClose={handleCloseIndicationDetails}
        indication={indicationOrQuote || currentIndication}
      />
    )
    setTimeout(() => {
      setSidebar!(null)
    }, 300)
  }

  // A carrier that stopped and asked for a document is the only one here the customer can
  // unblock, so it leads the carriers that want nothing from them. Sort is stable, so the
  // rest keep the order they came back in.
  const pendingIndications = enrichedIndications
    .filter((i) => i.status === InsuranceIndicationStatus.PENDING)
    .slice()
    .sort(
      (a, b) =>
        Number((b.outstandingDocuments ?? []).length > 0) - Number((a.outstandingDocuments ?? []).length > 0)
    )

  // What the customer picked comes first, cheapest-first within each group: once a
  // checkout has happened, the applied carriers are what the conversation is about.
  const nonPendingIndications = enrichedIndications
    .filter((i) => i.status !== InsuranceIndicationStatus.PENDING && i.isCardVisible)
    .slice()
    .sort((a, b) => {
      if (!!a.dateApplied !== !!b.dateApplied) return a.dateApplied ? -1 : 1
      if (!a.premium && !b.premium) return 0
      if (!a.premium) return 1
      if (!b.premium) return -1
      return a.premium - b.premium
    })

  // A carrier that has given its final answer goes last, whatever that answer was.
  // Sorting only on PENDING buried a carrier still actively underwriting beneath a
  // column of declines, which is the opposite of what anyone opens this list for.
  const offeredIndications = nonPendingIndications.filter((i) => !TERMINAL_STATUSES.has(i.status))
  const closedIndications = nonPendingIndications.filter((i) => TERMINAL_STATUSES.has(i.status))

  const renderOfferCard = (indication: (typeof enrichedIndications)[number]) => (
    <ChatIndicationCard
      key={indication.id}
      indication={indication}
      isSelected={selected.some((i) => i.id === indication.id)}
      hideCheckbox={hideCheckbox || !!readonly}
      hideViewDetails={hideViewDetails}
      onToggleSelect={handleToggleSelect}
      onViewDetails={handleOpenIndicationDetails}
      showQuotes={showQuotes}
      isStaff={isStaff}
      isCaiAdmin={isCaiAdmin}
      applicationId={applicationId}
      orgId={orgId}
      applicationEditedAt={applicationEditedAt}
      readonly={readonly}
    >
      {renderIndicationChildren?.(indication)}
    </ChatIndicationCard>
  )

  return (
    <>
      {offeredIndications.map(renderOfferCard)}
      {pendingIndications.map((indication) => (
        <ChatIndicationCard
          key={indication.id}
          indication={indication}
          isSelected={false}
          hideCheckbox={true}
          hideViewDetails={hideViewDetails}
          onToggleSelect={handleToggleSelect}
          onViewDetails={handleOpenIndicationDetails}
          isStaff={isStaff}
          isCaiAdmin={isCaiAdmin}
          applicationId={applicationId}
          orgId={orgId}
          applicationEditedAt={applicationEditedAt}
          readonly={readonly}
        >
          {renderIndicationChildren?.(indication)}
        </ChatIndicationCard>
      ))}
      {closedIndications.map(renderOfferCard)}
      {isStaff && effectivePortalLinks && effectivePortalLinks.length > 0 && (
        <div className="flex flex-col items-end gap-2">
          {effectivePortalLinks.map((link) => (
            <a
              key={link.sourceLabel}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-ink hover:text-ink/80 flex items-center gap-1 text-sm font-medium"
            >
              View {link.providerLabel} Portal ({link.sourceLabel}) - {link.statusLabel}
              <ExternalLink className="h-3 w-3" />
            </a>
          ))}
        </div>
      )}
    </>
  )
}
